import type { PreviewDemo, PreviewFocusMessage, PreviewPlace } from "@innoclouds/ui";
import { find, first, hasText, isOpen, shownIn, tap, waitFor } from "./dom";
import { addToCart, buy, findCard, openProduct } from "./menu";

export const ABSENT = Symbol("absent");

export interface Place extends PreviewPlace {
	enter: (demo: PreviewDemo) => Promise<unknown>;
	seek?: (demo: PreviewDemo) => Promise<unknown>;
	scopes: () => HTMLElement[];
}

const sections = () => find("main", "section");

const isLikedSection = (section: HTMLElement) => !!section.querySelector(".most_liked_all_title");

const panel = (css: string) => () => [shownIn(css)].filter((el): el is HTMLElement => !!el);

const MENU: Place = {
	id: "menu",
	title: "Меню",
	enter: () => waitFor(() => isOpen("main")),
	seek: async (demo) => demo.subjects[0] && findCard(demo.subjects[0], demo),
	scopes: () => sections().filter((section) => !isLikedSection(section)),
};

const LIKED: Place = {
	id: "liked",
	title: "Самые залайканные",
	enter: async () => {
		await waitFor(() => isOpen("main"));
		const heart = await waitFor(() => find("main", "rail_item").find((item) => hasText(item, "♥")), 3000);
		if (!heart) return ABSENT;
		tap(heart);
		return waitFor(() => sections().find(isLikedSection));
	},
	scopes: () => sections().filter(isLikedSection),
};

const demoToken = () => {
	const part = (value: object) => btoa(JSON.stringify(value)).replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_");
	return `${part({ alg: "none", typ: "JWT" })}.${part({ exp: 4102444800 })}.preview`;
};

const FAVORITES: Place = {
	id: "favorites",
	title: "Избранное",
	enter: async () => {
		if (!localStorage.getItem("accessToken")) {
			const token = demoToken();
			localStorage.setItem("accessToken", token);
			localStorage.setItem("refreshToken", token);
			window.location.reload();
			return new Promise(() => {});
		}
		tap(await waitFor(() => first("main", "header_favorites")));
		return waitFor(() => shownIn(".favorites_panel"));
	},
	scopes: panel(".favorites_panel"),
};

const RECOMMENDATIONS: Place = {
	id: "recommendations",
	title: "Рекомендации",
	enter: async (demo) => {
		if (!(await openProduct(demo.upsell, demo))) return null;
		await buy();
		return (await waitFor(() => shownIn(".recommendation_wrapper"))) ?? ABSENT;
	},
	scopes: panel(".recommendation_wrapper"),
};

const COMBO: Place = {
	id: "combo",
	title: "Комбо",
	enter: async (demo) => {
		if (!(await openProduct(demo.combo, demo))) return null;
		await buy();
		return (await waitFor(() => shownIn(".popup_overlay_combo"))) ?? ABSENT;
	},
	scopes: panel(".popup_overlay_combo"),
};

const screenPlace = (screen: string, title: string, enter: Place["enter"]): Place => ({
	id: screen,
	title,
	enter,
	scopes: () => (isOpen(screen) ? [document.body] : []),
});

const SCREENS: Record<string, Place> = {
	main: screenPlace("main", "Главный экран", () => waitFor(() => isOpen("main"))),
	product_card: screenPlace("product_card", "Карточка блюда", async (demo) => demo.subjects[0] && openProduct(demo.subjects[0], demo)),
	cart: screenPlace("cart", "Корзина", async (demo) => {
		for (const subject of demo.subjects) await addToCart(subject, demo);
		tap(await waitFor(() => first("main", "cart_button")));
		return waitFor(() => isOpen("cart"));
	}),
	loyalty_auth: screenPlace("loyalty_auth", "Вход в лояльность", async () => {
		tap(await waitFor(() => first("main", "header_loyalty")));
		return waitFor(() => isOpen("loyalty_auth"));
	}),
};

/** Места, куда превью может перейти по просьбе конструктора. Без избранного: оно перезагружает страницу */
export const NAVIGABLE: Record<string, Place> = {
	main: MENU,
	liked: LIKED,
	recommendations: RECOMMENDATIONS,
	combo: COMBO,
	product_card: SCREENS.product_card,
	cart: SCREENS.cart,
	loyalty_auth: SCREENS.loyalty_auth,
};

export const placesFor = ({ screen, element, demo }: PreviewFocusMessage): Place[] => {
	if (screen === "main" && (element === "card" || element.startsWith("card_")))
		return [MENU, LIKED, FAVORITES, demo.upsell ? RECOMMENDATIONS : null, demo.combo ? COMBO : null].filter((place): place is Place => !!place);
	if (screen === "main" && (element.startsWith("section") || element === "category_title")) return [MENU, LIKED];
	if (screen === "main" && element.startsWith("recommendation")) return demo.upsell ? [RECOMMENDATIONS] : [];
	const place = SCREENS[screen];
	return place ? [place] : [];
};
